<template>
  <BaseTable
    :isLoading="isLoading"
    :tableData="data"
    :totalItemsCount="totalItemsCount"
    :perPage="perPage"
    :currentPage="currentPage"
    :showSearch="true"
    searchPlaceholder="Search by name"
    :showCreateButton="rolPermissions.isAllowedCreate"
    createLabel="New Accessorial"
    @create="$emit('openModal', { modalType: 'Create' })"
    @search="onSearch"
    @clear="onClear"
    @paginate="paginate"
  >
    <template #filters>
      <b-button
        class="data-table__container-filter--button-filter"
        @click="toggleInfoByType"
      >
        <b-icon icon="funnel" aria-hidden="true"></b-icon>
        {{ getInfoByType ? `Accessorial By ${buttonName}` : `All Accessorial` }}
      </b-button>
    </template>

    <template #columns>
      <Column field="id" header="Id" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.id }}</p>
        </template>
      </Column>
      <Column field="name" header="Name" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.accessorial_name || data.name }}</p>
        </template>
      </Column>
      <Column field="type" header="Type" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          {{ data.accessorial_type || data.type }}
        </template>
      </Column>
    </template>

    <template #actions-column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="data-table__actions">
            <span
              v-if="
                rolPermissions.isAllowedEdit ||
                (loggedUser && (loggedUser.role === 'global_admin' ||
                  (loggedUser.role !== 'global_admin' && getInfoByType)))
              "
              @click="$emit('openModal', { rowInfo: data, modalType: 'Edit' })"
            >
              <i class="pi pi-pencil"></i>
            </span>
            <span
              v-if="
                rolPermissions.isAllowedDelete ||
                (loggedUser && (loggedUser.role === 'global_admin' ||
                  (loggedUser.role !== 'global_admin' && !getInfoByType)))
              "
              @click="deleteRow(data)"
            >
              <i class="pi pi-trash"></i>
            </span>
          </div>
        </template>
      </Column>
    </template>
  </BaseTable>
</template>

<script>
import { mapGetters } from "vuex";
import { successMessage, deleteMessage } from "@/utils/alertMessages";
import { rolPermissions } from "@/mixins/itemsTable/infoUser";
import BaseTable from "./BaseTable.vue";

export default {
  name: "AccessorialTable",
  components: { BaseTable },
  emits: ["openModal"],
  data() {
    return {
      isLoading: false,
      data: [],
      loggedUser: null,
      rolPermissions: {},
      profileActive: null,
      currentCompanyDepartment: null,
      totalItemsCount: 0,
      currentPage: 1,
      perPage: 15,
      getInfoByType: true,
      buttonName: null,
      searchTerm: null,
    };
  },
  computed: {
    ...mapGetters("user", ["showCurrentCompanyDepartment"]),
  },
  watch: {
    async showCurrentCompanyDepartment() {
      if (this.showCurrentCompanyDepartment) {
        this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
        this.getButtonName();
        this.currentPage = 1;
        await this.updateInfoByType();
      }
    },
  },
  async created() {
    this.loggedUser = JSON.parse(localStorage.getItem("user-info"));
    this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
    this.getUserPermissions();
    this.getButtonName();
    this.getInfoByType = true;
    await this.getInfoProfile();
    await this.getAllAccessorials();
  },
  methods: {
    getUserPermissions() {
      this.rolPermissions = rolPermissions(this.loggedUser, "Accessorial");
    },
    getButtonName() {
      this.buttonName = this.currentCompanyDepartment.chosenDepartment.id === -1
        ? this.currentCompanyDepartment.chosenCompany.name
        : this.currentCompanyDepartment.chosenDepartment.name;
    },
    async getInfoProfile() {
      this.isLoading = true;
      const payload = {
        company_id: this.currentCompanyDepartment.chosenCompany.id,
        department_id: this.currentCompanyDepartment.chosenDepartment.id,
      };
      const response = await this.$store.dispatch("company/getProfileCompanysDepartment", payload);
      this.profileActive = response.status === 200 ? response.data : null;
      this.isLoading = false;
    },
    async getAllAccessorials(params) {
      this.isLoading = true;
      const payload = { limit: 15, skip: (this.currentPage - 1) * 15 };
      if (params?.name) payload.name = params.name;
      const response = await this.$store.dispatch("accessorial/getAllAccessorials", payload);
      this.data = response.data;
      this.totalItemsCount = response.count;
      this.isLoading = false;
    },
    async getAccessorialsByCurrentInfo(params) {
      this.isLoading = true;
      const payload = {
        company_id: this.currentCompanyDepartment.chosenCompany.id,
        profile_id: this.profileActive.data[0].profile_id,
        department_id: this.currentCompanyDepartment.chosenDepartment.id,
        limit: 15,
        skip: (this.currentPage - 1) * 15,
      };
      if (params?.name) payload.accessorial_name = params.name;
      const response = await this.$store.dispatch("accessorial/getAccessorialsByProfile", payload);
      this.data = response.data;
      this.totalItemsCount = response.count;
      this.isLoading = false;
    },
    async updateInfoByType(params) {
      if (this.getInfoByType) {
        await this.getAllAccessorials(params);
      } else {
        await this.getAccessorialsByCurrentInfo(params);
      }
    },
    toggleInfoByType() {
      this.getInfoByType = !this.getInfoByType;
      this.updateInfoByType();
    },
    async onSearch(term) {
      if (term) {
        this.searchTerm = term;
        this.currentPage = 1;
        await this.updateInfoByType({ name: term });
      }
    },
    async onClear() {
      this.searchTerm = null;
      this.currentPage = 1;
      await this.updateInfoByType();
    },
    async paginate(page) {
      this.currentPage = page;
      await this.updateInfoByType(this.searchTerm ? { name: this.searchTerm } : undefined);
      window.scrollTo(0, 0);
    },
    async deleteRow(accessorialData) {
      const validateRole = !this.getInfoByType;
      const msg = validateRole
        ? `Are you sure you want to remove the Accessorial from the ${this.buttonName} profile?`
        : "Are you sure you want to remove Accessorial from the system?";
      const warningResponse = await deleteMessage(msg, "You won't be able to revert this!");
      if (warningResponse.isConfirmed) {
        let response;
        if (this.loggedUser.role === "global_admin" && this.getInfoByType) {
          response = await this.$store.dispatch(
            "accessorial/deleteAccessorial",
            accessorialData.accessorial_id || accessorialData.id,
          );
        } else {
          const params = {
            accessorial_id: accessorialData.accessorial_id || accessorialData.id,
            profile_id: this.profileActive.data[0].profile_id,
            company_id: this.currentCompanyDepartment.chosenCompany.id,
            department_id: this.currentCompanyDepartment.chosenDepartment.id,
          };
          response = await this.$store.dispatch("accessorial/deleteAccessorialByProfile", params);
        }
        if (response.status === 204 || response.status === 201) {
          successMessage("Accessorial has been deleted");
          if (this.data.length === 1 && this.currentPage > 1) this.currentPage -= 1;
          await this.updateInfoByType();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include table-action-icons;
</style>
